import * as pdfMake from 'pdfmake/build/pdfmake';
import { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import logoImg from '../../../assets/logo-black.png';

// Relatório Anual de Auditoria — PDF no padrão visual 2.0 (mesmo do
// Relatório de Atendimento): logo da unidade, faixas azuis, tabelas por
// mês e por motorista com a quebra por tipo de parada.

const AZUL = '#17457c';
const CINZA_CLARO = '#eef3f6';
const BORDA = '#dde4e6';
const LARGURA = 762; // A4 paisagem com margens de 40

export const TIPO_LABELS: Record<string, string> = {
  delivery: 'Entrega',
  collect: 'Coleta/Retirada',
  reverse: 'Reversa',
  devolution: 'Devolução',
  cleaning: 'Higienização',
  maintenance: 'Reparo',
  other: 'Outros',
};

const TIPO_KEYS = Object.keys(TIPO_LABELS);

export interface IAnnualAudit {
  companyDocument: string;
  year: number;
  partial: boolean;
  generatedAt: string;
  counters: {
    vehicles: number;
    drivers: number;
    routes: number;
    stops: number;
    routesPerDay: number;
    stopsPerRoute: number;
  };
  totalByType: Record<string, number>;
  monthly: {
    month: number;
    routes: number;
    days: number;
    routesPerDay: number;
    stops: number;
    stopsPerRoute: number;
    byType: Record<string, number>;
  }[];
  byDriver: {
    driverName: string;
    routes: number;
    days: number;
    months: number;
    stops: number;
    stopsPerRoute: number;
    byType: Record<string, number>;
  }[];
}

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const fmt = (n: number, dec = 0) =>
  Number(n).toLocaleString('pt-BR', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });

const th = (text: string, left = false) => ({
  text,
  fontSize: 6.5,
  bold: true,
  color: AZUL,
  fillColor: CINZA_CLARO,
  alignment: left ? ('left' as const) : ('right' as const),
});

const td = (text: string, opts: any = {}) => ({
  text,
  fontSize: 7,
  alignment: 'right' as const,
  ...opts,
});

const faixa = (texto: string): Content => ({
  margin: [0, 12, 0, 4],
  stack: [
    {
      canvas: [
        { type: 'rect', x: 0, y: 0, w: LARGURA, h: 20, r: 4, color: AZUL },
      ],
    },
    {
      text: texto,
      color: '#ffffff',
      bold: true,
      fontSize: 9,
      margin: [8, -14.5, 8, 7] as [number, number, number, number],
    },
  ],
});

const layoutTabela = {
  hLineColor: () => BORDA,
  vLineColor: () => BORDA,
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 2.5,
  paddingBottom: () => 2.5,
};

const docDefinition = (
  logo: string,
  data: IAnnualAudit
): TDocumentDefinitions => {
  const agora = new Date();
  const emitido = `${agora.toLocaleDateString('pt-BR')} ${agora
    .toLocaleTimeString('pt-BR')
    .slice(0, 5)}`;
  const cnpj = data.companyDocument.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );

  // função de propósito: o pdfmake MUTA os objetos de célula ao renderizar;
  // reutilizar as mesmas instâncias numa segunda tabela deixa o cabeçalho
  // em branco — cada tabela precisa de células novas
  const headerTipos = () => TIPO_KEYS.map((k) => th(TIPO_LABELS[k]));

  const linhasMes = data.monthly.map((m) => [
    td(MESES[m.month - 1] + (data.partial && m.month === agora.getMonth() + 1 ? ' (parcial)' : ''), {
      alignment: 'left',
      bold: true,
    }),
    td(fmt(m.routes)),
    td(fmt(m.routesPerDay, 1)),
    td(fmt(m.stops)),
    td(fmt(m.stopsPerRoute, 1)),
    ...TIPO_KEYS.map((k) => td(fmt(m.byType[k] || 0))),
  ]);
  const totalMes = [
    td(`Total ${data.year}`, {
      alignment: 'left',
      bold: true,
      color: AZUL,
      fillColor: CINZA_CLARO,
    }),
    ...[
      fmt(data.counters.routes),
      fmt(data.counters.routesPerDay, 1),
      fmt(data.counters.stops),
      fmt(data.counters.stopsPerRoute, 1),
      ...TIPO_KEYS.map((k) => fmt(data.totalByType[k] || 0)),
    ].map((v) => td(v, { bold: true, color: AZUL, fillColor: CINZA_CLARO })),
  ];

  const linhasMotorista = data.byDriver.map((d) => [
    td(d.driverName, { alignment: 'left', bold: true }),
    td(fmt(d.routes)),
    td(fmt(d.months ? d.routes / d.months : 0, 1)),
    td(fmt(d.days ? d.routes / d.days : 0, 1)),
    td(fmt(d.stops)),
    td(fmt(d.stopsPerRoute, 1)),
    ...TIPO_KEYS.map((k) => td(fmt(d.byType[k] || 0))),
  ]);

  const kpi = (n: string, l: string) => ({
    stack: [
      { text: n, fontSize: 14, bold: true, color: AZUL, alignment: 'center' as const },
      {
        text: l,
        fontSize: 6.5,
        bold: true,
        color: '#5c7276',
        alignment: 'center' as const,
      },
    ],
  });

  return {
    info: {
      title: `Relatório Anual de Auditoria ${data.year}`,
      author: '4LogX',
      creationDate: agora,
    },
    pageOrientation: 'landscape',
    pageMargins: [40, 36, 40, 40],
    footer: (currentPage, pageCount) => ({
      margin: [40, 10, 40, 0],
      columns: [
        {
          text: 'Documento gerado pela plataforma 4LogX',
          fontSize: 7,
          color: '#999999',
        },
        {
          text: `página ${currentPage} de ${pageCount}`,
          fontSize: 7,
          color: '#999999',
          alignment: 'right',
        },
      ],
    }),
    content: [
      {
        columns: [
          { image: logo, fit: [150, 46] },
          {
            stack: [
              {
                text: `Relatório Anual de Auditoria · ${data.year}`,
                bold: true,
                fontSize: 15,
                alignment: 'right',
              },
              {
                text: `emitido em ${emitido} · 4LogX${data.partial ? ' · ano em andamento (parcial)' : ''}`,
                fontSize: 7,
                color: '#888888',
                alignment: 'right',
                margin: [0, 3, 0, 0],
              },
            ],
            margin: [0, 8, 0, 0],
          },
        ],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 6,
            x2: LARGURA,
            y2: 6,
            lineWidth: 2.5,
            lineColor: AZUL,
          },
        ],
        margin: [0, 0, 0, 8],
      },
      {
        table: {
          widths: ['*', 'auto'],
          body: [
            [
              {
                stack: [
                  {
                    text: 'UNIDADE (TENANT)',
                    fontSize: 6.5,
                    bold: true,
                    color: AZUL,
                  },
                  { text: cnpj, fontSize: 10, bold: true },
                ],
              },
              {
                columns: [
                  kpi(fmt(data.counters.vehicles), 'VEÍCULOS'),
                  kpi(fmt(data.counters.drivers), 'MOTORISTAS'),
                  kpi(fmt(data.counters.routes), 'ROTAS'),
                  kpi(fmt(data.counters.stops), 'PARADAS'),
                  kpi(fmt(data.counters.routesPerDay, 1), 'ROTAS/DIA'),
                  kpi(fmt(data.counters.stopsPerRoute, 1), 'PARADAS/ROTA'),
                ],
                columnGap: 14,
                width: 'auto',
              },
            ],
          ],
        },
        layout: {
          ...layoutTabela,
          paddingTop: () => 6,
          paddingBottom: () => 6,
          paddingLeft: () => 10,
          paddingRight: () => 10,
        },
      },
      faixa('1 · ROTAS E PARADAS POR MÊS'),
      {
        table: {
          headerRows: 1,
          widths: [
            70,
            'auto',
            'auto',
            'auto',
            'auto',
            '*',
            '*',
            '*',
            '*',
            '*',
            '*',
            '*',
          ],
          body: [
            [
              th('Mês', true),
              th('Rotas'),
              th('Rotas/dia'),
              th('Paradas'),
              th('Paradas/rota'),
              ...headerTipos(),
            ],
            ...linhasMes,
            totalMes,
          ],
        },
        layout: layoutTabela,
      },
      faixa('2 · POR MOTORISTA'),
      {
        table: {
          headerRows: 1,
          widths: [
            120,
            'auto',
            'auto',
            'auto',
            'auto',
            'auto',
            '*',
            '*',
            '*',
            '*',
            '*',
            '*',
            '*',
          ],
          body: [
            [
              th('Motorista', true),
              th('Rotas'),
              th('Rotas/mês'),
              th('Rotas/dia'),
              th('Paradas'),
              th('Paradas/rota'),
              ...headerTipos(),
            ],
            ...linhasMotorista,
          ],
        },
        layout: layoutTabela,
      },
      {
        text:
          'Detalhamento dia a dia e rota a rota disponível nos anexos ' +
          `rotas-por-dia-${data.year}.csv e paradas-por-rota-${data.year}.csv, ` +
          'gerados pela mesma fonte deste relatório.',
        fontSize: 7,
        color: '#888888',
        margin: [0, 10, 0, 0],
      },
    ],
    defaultStyle: { fontSize: 8 },
  };
};

export async function generateAnnualAuditPDF(
  data: IAnnualAudit,
  companyLogoBase64?: string | null
) {
  let logo = companyLogoBase64 || '';
  if (!logo || !logo.startsWith('data:image')) {
    const imgBlob = await fetch(logoImg).then((res) => res.blob());
    logo = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result));
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(imgBlob);
    });
  }

  pdfMake
    .createPdf(docDefinition(logo, data))
    .download(`auditoria-anual-${data.year}.pdf`);
}
